/*
 * @format
 *
 * Garbage-collects the settling overlay (see desired-state-reducer.js). The
 * overlay retains a just-completed control goal's value so an OBSERVING phone
 * does not flicker to its stale reported value in the gap before its own
 * telemetry lands. getSharedPending self-heals the DISPLAY the moment reported
 * matches, but the stored entry must also be cleared so entries never
 * accumulate.
 *
 * The desired-state slice is deliberately isolated from reported/telemetry
 * state, so the reducer cannot itself decide "reported has caught up". This
 * saga bridges that: on any reported/telemetry action it re-reads both slices,
 * and for every settling entry that either (a) matches this phone's reported
 * zone state or (b) has outlived the TTL backstop, it dispatches a prune
 * carrying only the ids to remove — keeping the reported comparison out of the
 * slice.
 *
 * It watches a superset of the actions that can move reported state
 * (WEBSOCKET_LIVE_RECEIVED and the two bootstrap folds rebuild homeList zones;
 * the PRT-live / live-data telemetry ticks give a periodic, home-agnostic beat
 * that also enforces the TTL even when the confirming telemetry for a given
 * zone never arrives). Re-reading full state on each makes watching extra
 * actions harmless — an entry is pruned only when the shared rule says so.
 */
import { all, put, select, takeEvery } from 'redux-saga/effects';

import * as types from '../action-types';
import { SETTLING_TTL_MS } from '../reducers/desired-state-reducer';
import { reportedMatchesSettling } from '../selectors/desired-state-selectors';

// The reported/telemetry actions that can settle (or periodically re-check) the
// overlay. WEBSOCKET_LIVE_RECEIVED and the bootstraps rebuild homeList zones;
// the telemetry ticks drive the TTL backstop.
const REPORTED_ACTIONS = [
  types.WEBSOCKET_LIVE_RECEIVED,
  types.HOMES_MESSAGE_RECEIVED,
  types.WEBSOCKET_HELLO_MESSAGE_RECEIVED,
  types.WEBSOCKET_PRT_LIVE_RECEIVED,
  types.WEBSOCKET_LIVE_DATA_RECEIVED,
];

export function* pruneSettledEntries() {
  const state = yield select();
  const desired = state.desiredState;

  if (!desired) {
    return;
  }

  const now = Date.now();

  const prunes = Object.keys(desired)
    .map(interfaceId => {
      const entry = desired[interfaceId];
      const settling = entry && entry.settling;

      if (!Array.isArray(settling) || settling.length === 0) {
        return null;
      }

      const clientRequestIds = settling
        .filter(
          s =>
            s &&
            (now - s.settledAt > SETTLING_TTL_MS ||
              reportedMatchesSettling(state, interfaceId, s)),
        )
        .map(s => s.clientRequestId);

      return clientRequestIds.length > 0
        ? { interfaceId, clientRequestIds }
        : null;
    })
    .filter(Boolean);

  if (prunes.length === 0) {
    return;
  }

  yield all(
    prunes.map(({ interfaceId, clientRequestIds }) =>
      put({
        type: types.DESIRED_STATE_SETTLING_PRUNED,
        interfaceId,
        clientRequestIds,
      }),
    ),
  );
}

export default function* desiredStateSettling() {
  yield all([takeEvery(REPORTED_ACTIONS, pruneSettledEntries)]);
}
